(() => {
  'use strict';
  const blocked = () => document.querySelector('[data-vf-shipping-blocked]');
  const root = window.Shopify?.routes?.root || '/';
  const busy = () => document.querySelector('.cart__items--disabled, [data-vf-cut-selector] .submitting');
  let running = false;
  let timer;
  let started = 0;
  let revision = 0;
  let requestNumber = 0;

  const ready = (cart) => Array.isArray(cart?.items) && cart.items.every((item) => {
    if (!item.properties?.['Requested cut length']) return true;
    const expected = Number(item.properties['_Estimated material weight grams']);
    const native = Number(item.grams);
    const legacy = !item.properties['_Expected cut price cents'] && !item.properties['_Cut price currency'];
    const expectedPrice = Number(legacy
      ? cart.vf_legacy_prices?.find((entry) => entry.key === item.key)?.price
      : item.properties['_Expected cut price cents']);
    const priceReady = Number.isSafeInteger(expectedPrice) && expectedPrice > 0
      && Number(item.original_price) === expectedPrice
      && (legacy || item.properties['_Cut price currency'] === cart.currency);
    return Number.isFinite(expected) && Number.isFinite(native)
      && expected > 0 && native > 0 && Math.abs(expected - native) <= 5
      && priceReady;
  });

  const request = async (input, format = 'json') => {
    const url = new URL(input, window.location.origin);
    url.searchParams.set('_vf_cart_refresh', `${Date.now()}-${++requestNumber}`);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, {
        cache: 'no-store', credentials: 'same-origin',
        headers: { Accept: format === 'json' ? 'application/json' : 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Cart unavailable');
      return await response[format]();
    } finally {
      clearTimeout(timeout);
    }
  };

  const setStatus = (checking) => {
    document.querySelectorAll('[data-vf-shipping-blocked]').forEach((notice) => {
      const message = notice.querySelector('[data-vf-weight-message]');
      const text = checking ? notice.dataset.pendingMessage : notice.dataset.issueMessage;
      if (message && text && message.textContent !== text) message.textContent = text;
      const retry = notice.querySelector('[data-vf-weight-retry]');
      if (retry) retry.disabled = checking;
    });
  };

  const refreshSections = async (cart, current) => {
    const targets = [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section', optional: true },
      { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
      { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.cart__blocks' },
    ].filter((target) => target.section && document.getElementById(target.id));
    if (!targets.length) return false;
    const parser = new DOMParser();
    const prepare = (sourceFor) => targets.map((target) => {
      const source = sourceFor(target);
      const parent = document.getElementById(target.id);
      const destination = parent?.querySelector(target.selector) || parent;
      if (!source || !destination) {
        if (target.optional) return null;
        throw new Error('Cart display unavailable');
      }
      if (source.querySelector('[data-vf-shipping-blocked]')) throw new Error('Cart still updating');
      return { source, destination };
    }).filter(Boolean);
    let replacements;
    try {
      const url = new URL(root + 'cart', window.location.origin);
      url.searchParams.set('sections', targets.map((target) => target.section).join(','));
      const sections = await request(url);
      replacements = prepare((target) => sections[target.section]
        ? parser.parseFromString(sections[target.section], 'text/html').querySelector(target.selector) : null);
    } catch {
      const html = parser.parseFromString(await request(root + 'cart', 'text'), 'text/html');
      replacements = prepare((target) => {
        const parent = html.getElementById(target.id);
        return parent?.querySelector(target.selector) || parent;
      });
    }
    if (revision !== current || busy() || document.hidden) return false;
    const focus = document.activeElement;
    const focusId = focus?.id;
    const losesFocus = replacements.some(({ destination }) => destination.contains(focus));
    const notes = [...document.querySelectorAll('textarea[name="note"]')].map((note) => ({ id: note.id, value: note.value }));
    replacements.forEach(({ source, destination }) => { destination.innerHTML = source.innerHTML; });
    notes.forEach((note) => { const element = document.getElementById(note.id); if (element) element.value = note.value; });
    document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items, #main-cart-footer')
      .forEach((element) => element.classList.toggle('is-empty', cart.item_count === 0));
    const drawer = document.querySelector('cart-drawer.active');
    if (drawer && typeof trapFocus === 'function') {
      const container = drawer.querySelector('.drawer__inner');
      const nextFocus = (focusId && document.getElementById(focusId)) || container?.querySelector('.drawer__close') || container;
      trapFocus(container, losesFocus ? nextFocus : focus);
    } else if (losesFocus && focusId) {
      document.getElementById(focusId)?.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('vf:cart-ready', { detail: cart }));
    return !blocked();
  };

  const schedule = (delay = 150) => {
    if (running || timer || document.hidden || !blocked()) return;
    timer = setTimeout(() => { timer = null; void recover(); }, delay);
  };
  const recover = async () => {
    if (running || document.hidden || !blocked()) return;
    running = true;
    if (!started) started = Date.now();
    const current = revision;
    setStatus(Date.now() - started < 60000);
    try {
      if (busy()) return;
      let cart = await request(root + 'cart.js');
      if (cart.items?.some((item) => item.properties?.['Requested cut length'] && !item.properties['_Expected cut price cents'] && !item.properties['_Cut price currency'])) {
        const verified = JSON.parse(await request(root + 'cart?view=vf-cut-verification', 'text'));
        if (!Array.isArray(verified.cart?.items) || !Array.isArray(verified.legacyPrices)) throw new Error('Cart verification unavailable');
        cart = verified.cart;
        cart.vf_legacy_prices = verified.legacyPrices;
      }
      if (ready(cart) && revision === current && await refreshSections(cart, current)) started = 0;
    } catch {
    } finally {
      running = false;
      if (blocked()) {
        const delayed = Date.now() - started >= 60000;
        setStatus(!delayed);
        schedule(delayed ? 10000 : 1000);
      }
    }
  };
  const retryNow = () => {
    clearTimeout(timer);
    timer = null;
    started = 0;
    schedule();
  };

  const refreshPage = () => {
    try {
      if (document.querySelector('cart-drawer.active')) {
        sessionStorage.setItem('vf:cart-refresh-drawer', window.location.pathname + window.location.search);
      } else {
        sessionStorage.removeItem('vf:cart-refresh-drawer');
      }
    } catch {}
    window.location.reload();
  };

  const restoreDrawer = () => {
    try {
      const previousPage = sessionStorage.getItem('vf:cart-refresh-drawer');
      sessionStorage.removeItem('vf:cart-refresh-drawer');
      if (previousPage !== window.location.pathname + window.location.search) return;
      if (!document.querySelector('cart-drawer')) return;
      customElements.whenDefined('cart-drawer').then(() => {
        document.querySelector('cart-drawer')?.open(document.getElementById('cart-icon-bubble'));
      });
    } catch {}
  };

  document.addEventListener('change', (event) => {
    if (event.target.closest('cart-items, cart-drawer-items')) revision += 1;
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-vf-cart-refresh]')) {
      event.preventDefault();
      refreshPage();
      return;
    }
    if (event.target.closest('cart-remove-button, [data-vf-submit]')) revision += 1;
    if (event.target.closest('[data-vf-weight-retry]')) {
      event.preventDefault();
      retryNow();
    }
    if (event.target.closest('[name="checkout"]') && blocked()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      blocked().scrollIntoView({ block: 'center' });
      retryNow();
    }
    if (event.target.closest('#cart-icon-bubble')) retryNow();
  }, true);
  document.addEventListener('submit', (event) => {
    if (!event.target.matches('#cart, #CartDrawer-Form') || !blocked()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    blocked().scrollIntoView({ block: 'center' });
    retryNow();
  }, true);
  const start = () => {
    restoreDrawer();
    const observer = new MutationObserver(() => schedule());
    ['CartDrawer', 'main-cart-footer', 'main-cart-items'].forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element, { childList: true, subtree: true });
    });
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('pageshow', retryNow);
  window.addEventListener('online', retryNow);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) retryNow(); });
})();


